import { useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ValueFormatterParams, CellClassParams } from 'ag-grid-community';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import { format, isWithinInterval, parseISO } from 'date-fns';
import { EmployeeWithPrioritiesDto } from '../../api';
import '../../styles/agGridStyles.css';
import enNZ from 'date-fns/esm/locale/en-NZ/index.js';
import { renderUtilisation, renderStatus } from '../../components/datagrid/ag-grid';
import Avatar from '@mui/material/Avatar';
import { Grid, Box, CircularProgress, CircularProgressProps, Typography } from '@mui/material';

interface EmployeeDisplayGridProps {
  employee: EmployeeWithPrioritiesDto;
}

function ProgressGraphWithLabel(props: CircularProgressProps & { value: number }) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" color="text.secondary">
          {`${props.value}%`}
        </Typography>
      </Box>
    </Box>
  );
}

function ProgressGraph(props: number) {
  let progress = props;
  return <ProgressGraphWithLabel value={progress} />;
}

/**
 * Defines rules for applying CSS classes to the start and end dates.
 */
const dateCellRules = {
  'project-active': (params: CellClassParams) => {
    const today = new Date();
    const startDate = parseISO(params.data.startDate);
    const endDate = parseISO(params.data.endDate);
    return isWithinInterval(today, { start: startDate, end: endDate });
  },
};

function EmployeeDisplayGrid(props: EmployeeDisplayGridProps) {
  const { employee } = props;
  const [columnDefs, setColumnDefs] = useState(getColumnDefs());

  if (!employee || !employee.priorities) {
    return <div>No Employee Priorities to display</div>;
  }

  //calculates total utilisation for all priorities
  let total = 0;
  for (let priority of employee.priorities) {
    total += priority.utilisation;
  }

  //gets employees initials for letter avatar
  let initials = employee.firstName?.charAt(0);
  if (!initials) {
    return <div>No initials to display</div>;
  }
  initials += employee.lastName?.charAt(0);

  /**
   * Formats the date to dd/mm/yyyy format
   * @param params ValueFormatterParams
   * @returns Date string in dd/mm/yyyy format
   */
  function dateFormatter(params: ValueFormatterParams) {
    const date: Date = parseISO(params.value);
    const formattedDate = format(date, 'dd/MM/yyyy', { locale: enNZ });
    return formattedDate;
  }

  function renderDateRange(params: ValueFormatterParams) {
    const startDate: Date = parseISO(params.data.startDate);
    const endDate: Date = parseISO(params.data.endDate);
    const formattedStartDate = format(startDate, 'dd/MM/yyyy', { locale: enNZ });
    const formattedEndDate = format(endDate, 'dd/MM/yyyy', { locale: enNZ });
    return `${formattedStartDate} - ${formattedEndDate}`;
  }

  //converts user name to rgb color
  function setColour() {
    let name = 0;
    if (employee.firstName && employee.lastName) {
      for (let i = 0; i < employee.firstName.length; i++) {
        name += employee.firstName.charCodeAt(i);
      }
      for (let j = 0; j < employee.lastName.length; j++) {
        name += employee.lastName.charCodeAt(j);
      }
    }
    let color = name * 10;
    let color1 = color % 255;
    let color2 = color % 155;
    let color3 = color % 55;
    return rgbToHex(color1, color2, color3);
  }

  //converts rgb color to hex
  function asciiToHex(c: number) {
    var hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }
  function rgbToHex(color1: number, color2: number, color3: number) {
    return '#' + asciiToHex(color1) + asciiToHex(color2) + asciiToHex(color3);
  }

  /**
   * Gets the column header definiton for the grid
   */
  function getColumnDefs() {
    return [
      {
        headerName: `${employee.firstName} ${employee.lastName}`,
        field: 'project.clientName',
        filter: true,
        autoHeight: true,
      },
      {
        headerName: '',
        field: 'status',
        filter: true,
        minWidth: 100,
        cellRenderer: renderStatus,
      },
      {
        headerName: '',
        field: 'project.name',
        filter: true,
        autoHeight: true,
        minWidth: 150,
      },
      {
        headerName: '',
        field: 'startDate',
        filter: true,
        autoHeight: true,
        minWidth: 250,
        valueFormatter: renderDateRange,
        cellClassRules: dateCellRules,
      },
      {
        colId: 'utilisation',
        headerName: '',
        field: 'utilisation',
        filter: true,
        autoHeight: true,
        minWidth: 100,
        cellRenderer: renderUtilisation,
      },
    ];
  }

  return (
    <Grid
      container
      columnSpacing={1}
      columns={16}
      justifyContent={'center'}
      alignItems="center"
      paddingTop="1%"
      paddingLeft="2%"
    >
      <Grid item>
        <Avatar sx={{ backgroundColor: setColour() }}>{initials}</Avatar>
        <Grid item paddingTop="20%">
          {ProgressGraph(total)}
        </Grid>
      </Grid>
      <Grid id="priorityGrid" item xs={14} paddingBottom="0.75%">
        <div className="ag-theme-alpine">
          {employee.priorities && (
            <AgGridReact
              rowData={employee.priorities}
              overlayNoRowsTemplate={'No Priorities to Display'}
              columnDefs={columnDefs}
              domLayout="autoHeight"
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
}

export default EmployeeDisplayGrid;
